import { anyReallyAny, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { TypedKey } from "../types/types.definition";
import { isType } from "../types/types.tools";

export interface AbstractHierarchy<T> {
    data: T;
    parent?: AbstractHierarchy<T>;
}


/**
 * Return a deepClone of given object, using a recursive implementation.
 * ! Be careful to cyclic object !
 * @param obj Object to clone
 * @param safetyDepth Optional. Depth at which the function should stop cloning. Default value is 10.
 * @returns A deep clone of given object
 * @throws If safetyDepth is inferior or equal to 0
 */
export function deepClone<T>(obj: T, safetyDepth = 10): T {
    if (safetyDepth <= 0) {
        throw Error("[deepClone] Deep clone depth reached...");
    }
    if (obj === null) {
        return null as unknown as T;
    } else if (typeof obj === "object" && Array.isArray(obj)) {
        return (safetyDepth - 1 <= 0 ? null : [...obj.map(v => deepClone(v, safetyDepth - 1))]) as unknown as T;
    } else if (typeof obj === "object") {
        if (safetyDepth - 1 <= 0) { return null as unknown as T; }
        const cpy = {};
        for (const k of Object.keys(obj)) {
            setProp(cpy, k, deepClone(getProp(obj, k), safetyDepth - 1));
        }
        return cpy as unknown as T;
    } else {
        return obj;
    }
}

/** */
export function addToObjectOfArray<T extends { [K in keyof T]: anyReallyAny[] }>(obj: T, k: keyof T, values: T[keyof T], noDuplicate = true): boolean {
    let added = false;
    if (obj[k] === undefined && values.length) {
        if (Array.isArray(values)) {
            obj[k] = [... new Set(values)] as T[keyof T];
        }
        added = true;
    } else {
        if (Array.isArray(values) && values.length) {
            if (noDuplicate) {
                for (const value of values) {
                    if (!obj[k].includes(value)) {
                        added = true;
                        obj[k].push(value);
                    }
                }
            } else {
                obj[k].push(...values);
                added = true;
            }
        }
    }
    return added;
}

/** Hierarchize anything with anything (almost) */
export function hierarchizeIt<
    Hierarchizable,
    NonHierarchizable,
    KHierarchizable extends keyof Hierarchizable,
    B extends (Hierarchizable | NonHierarchizable),
    K extends keyof B,
>(
    anyHierarchizable: Hierarchizable[],
    anyNonHierarchizable: NonHierarchizable[],
    mappers: {
        parentKey: KHierarchizable,
        targetKey: TypedKey<B, Hierarchizable[KHierarchizable]>,
        parentTypeKey: K,
        parentTypeValue: B[K]
    }[],
    filtersOnValueEquity?: K[]
): AbstractHierarchy<B>[] {
    const ahData: AbstractHierarchy<B>[] = ([...anyHierarchizable, ...anyNonHierarchizable] as B[]).map(v => ({ data: v }));
    for (const d of ahData) {
        const dataOfD = d.data;

        let filteredData = ahData;
        if (filtersOnValueEquity !== undefined) {
            filteredData = ahData.filter(v => {
                for (const filter of filtersOnValueEquity) {
                    if (dataOfD[filter] === v.data[filter]) {
                        return true;
                    }
                }
                return false;
            });
        }
        const parent = filteredData.find(v => mappers.some(w => isType<Hierarchizable>(dataOfD, w.parentKey) && v.data[w.parentTypeKey] === w.parentTypeValue && v.data[w.targetKey] === dataOfD[w.parentKey as unknown as keyof B]));
        d.parent = parent;

    }
    return ahData;
}

export type SUPPORTED_TYPEOF_CHECKS = "object" | "string" | "number" | "boolean" | "symbol" | "function" | "bigint"

/**
 * Retrieve `property` of `obj`, while performing a type check on `obj[property]`
 * @param obj Anything
 * @param property Property of `obj` to retrieve as a string
 * @param type Type of `obj[property]`. It will be checked with `typeof` function
 * @returns `obj[property]` if it exists and pass type check
 * @throws If `obj[property]` doesn't exist, or if `obj[property]` doesn't pass type check
 */
export function getPropertyTypeCheckedFromAny<T>(obj: anyReallyAny, property: string, type: SUPPORTED_TYPEOF_CHECKS): T {

    if (obj == null) {
        throw Error("Cannot get property " + property + " from null");
    }
    const tmp = obj[property];
    if (tmp == null) {
        throw Error("Cannot get property " + property);
    }
    if (typeof tmp !== type) {
        throw Error("Property " + property + " isn't of type " + type);
    }

    return tmp as T;
}

/**
 * Retrieve `obj[property]` if it exists within `obj` and passes a type check
 * @param obj Anything
 * @param property Property of `obj` to retrieve as a string
 * @param type Type of `obj[property]`. It will be checked with `typeof` function
 * @returns `obj[property]` if it exists and pass type check, otherwise `undefined`
 * @throws If `obj[property]` doesn't pass type check
 */
export function findPropertyTypeCheckedFromAny<T>(obj: anyReallyAny, property: string, type: SUPPORTED_TYPEOF_CHECKS): T | undefined {

    if (obj == null) {
        return undefined;
    }
    const tmp = obj[property];
    if (tmp == null) {
        return tmp;
    }
    if (typeof tmp !== type) {
        throw Error("Property " + property + " isn't of type " + type);
    }

    return tmp as T;
}